import { useEffect, useRef, useState } from "react";
import {
    Autocomplete,
    TextField,
    CircularProgress,
    Box,
    Typography,
} from "@mui/material";

export default function AsyncSearchDropdown({
    label,
    apiUrl,
    pageSize = 10,
    getOptionLabel = (option) => option?.name ?? "",
    onChange,
    style,
    value = null,
    disabled = false,
    multiple = false,
    filterOptions: customFilterOptions,
}) {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [inputValue, setInputValue] = useState("");

    const debounceRef = useRef(null);

    async function fetchOptions({ reset = false, searchText = inputValue }) {
        if (loading) return;

        setLoading(true);
        const currentPage = reset ? 1 : page;

        const params = new URLSearchParams({
            page: String(currentPage),
            page_size: String(pageSize),
            text: searchText,
        });

        // ensure we use the correct separator ("?" or "&") depending
        // whether apiUrl already has query parameters attached.
        const sep = apiUrl.includes("?") ? "&" : "?";
        const res = await fetch(`${apiUrl}${sep}${params.toString()}`, {
            credentials: "include",
        });
        const data = await res.json();

        setOptions((prev) => (reset ? data.items : [...prev, ...data.items]));
        setHasMore(currentPage * pageSize < data.total_count);
        setPage(currentPage + 1);
        setLoading(false);
    }

    useEffect(() => {
        if (open) {
            setPage(1);
            fetchOptions({ reset: true });
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;

        if (debounceRef.current) clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(() => {
            setPage(1);
            fetchOptions({ reset: true, searchText: inputValue });
        }, 350);

        return () => clearTimeout(debounceRef.current);
    }, [inputValue]);

    function handleScroll(e) {
        const node = e.currentTarget;
        const reachedBottom =
            node.scrollTop + node.clientHeight >= node.scrollHeight - 8;

        if (reachedBottom && hasMore && !loading) {
            fetchOptions({});
        }
    }

    return (
        <Autocomplete
            fullWidth
            sx={{
                ...style,
                width: "100%",
                minWidth: 200,
                mt: 1,
                mb: 1,
            }}
            open={open}
            multiple={multiple}
            value={value}
            disabled={disabled}
            clearOnBlur={false}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            options={options}
            loading={loading}
            filterOptions={customFilterOptions || ((x) => x)}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
            inputValue={inputValue}
            onInputChange={(_, val, reason) => {
                if (!multiple && reason === "input") {
                    setInputValue(val);
                }
            }}
            onChange={(_, val) => {
                onChange?.(val);
                if (!multiple) setInputValue(val ? getOptionLabel(val) : "");
            }}
            slotProps={{
                listbox: {
                    onScroll: handleScroll,
                    sx: { maxHeight: 280, overflow: "auto" },
                },
            }}
            loadingText="Loading..."
            noOptionsText={inputValue ? "No results" : "Start typing to search"}
            renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                    <Typography variant="body2">
                        {getOptionLabel(option)}
                    </Typography>
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder="Type to search"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading && <CircularProgress size={18} />}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
